<script setup>
import DesktopIcon from '@iconify-vue/ph/desktop';
import DeviceMobileIcon from '@iconify-vue/ph/device-mobile';
import EyeIcon from '@iconify-vue/ph/eye';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            header: () => 'Nom',
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href: route('front.projectManagement.dev.pageSpeed', {
                        tenant: props.row.original.id,
                    }),
                    inertia: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'urlCount',
            header: () => 'NB URLs',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.urlCount,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'averageDesktopScore',
            header: () => 'Score moyen bureau',
            cell: (props) =>
                props.row.original.averageDesktopScore
                    ? h(PillCell, {
                          pillValue: props.row.original.averageDesktopScore,
                          type:
                              props.row.original.averageDesktopScore >= 90
                                  ? 'success'
                                  : props.row.original.averageDesktopScore >= 50
                                    ? 'warning'
                                    : 'danger',
                          leftIcon: DesktopIcon,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'averageMobileScore',
            header: () => 'Score moyen mobile',
            cell: (props) =>
                props.row.original.averageMobileScore
                    ? h(PillCell, {
                          pillValue: props.row.original.averageMobileScore,
                          type:
                              props.row.original.averageMobileScore >= 90
                                  ? 'success'
                                  : props.row.original.averageMobileScore >= 50
                                    ? 'warning'
                                    : 'danger',
                          leftIcon: DeviceMobileIcon,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'maxCreatedAt',
            header: () => 'Dernière vérification le',
            cell: (props) =>
                props.row.original.maxCreatedAt
                    ? h(TextCell, {
                          text: formatDate(
                              props.row.original.maxCreatedAt,
                              false,
                              true,
                          ),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Voir',
                                href: route(
                                    'front.projectManagement.dev.pageSpeed',
                                    {
                                        tenant: props.row.original.id,
                                    },
                                ),
                                icon: EyeIcon,
                            }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.dev.postPageSpeed'),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    />
</template>
